export class GameAudio {
  private context: AudioContext | null = null;
  private enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  async unlock(): Promise<void> {
    if (!this.enabled) return;
    this.context ??= new AudioContext();
    if (this.context.state === "suspended") await this.context.resume();
  }

  grab(): void {
    this.tone(210, 0.045, "triangle", 0.035, 45);
  }

  launch(speed: number): void {
    this.tone(155 + Math.min(speed / 8, 180), 0.09, "sawtooth", 0.025, -70);
  }

  scream(speed: number, voice: number): void {
    if (speed < 480) return;
    const pitch = 380 + Math.min(speed / 4, 340) + voice * 23;
    this.tone(pitch, 0.14, "sawtooth", 0.013, 90, 0.02);
    this.tone(pitch * 1.07, 0.18, "triangle", 0.01, -120, 0.09);
  }

  splash(strength: number): void {
    const power = Math.min(1, Math.max(0.25, strength));
    this.tone(300, 0.16, "sine", 0.05 * power, -220);
    this.tone(950, 0.05, "square", 0.014 * power, -420, 0.015);
    this.tone(640, 0.07, "triangle", 0.02 * power, -300, 0.045);
    this.tone(180, 0.2, "sine", 0.04 * power, -110, 0.05);
  }

  deport(combo: number): void {
    const base = 470 + Math.min(combo, 12) * 34;
    this.tone(base, 0.1, "sine", 0.045, 200);
    this.tone(base * 1.5, 0.14, "sine", 0.035, 150, 0.07);
  }

  strike(): void {
    this.tone(150, 0.28, "sawtooth", 0.05, -70);
    this.tone(106, 0.34, "square", 0.032, -50, 0.06);
    this.tone(82, 0.4, "triangle", 0.04, -30, 0.12);
  }

  boatBeach(): void {
    this.tone(95, 0.22, "triangle", 0.07, -35);
    this.tone(140, 0.12, "square", 0.025, -60, 0.03);
  }

  gameOver(): void {
    const notes = [392, 330, 262, 196];
    notes.forEach((note, index) => {
      this.tone(note, 0.24, "triangle", 0.045, -18, index * 0.19);
    });
  }

  countdown(final: boolean): void {
    this.tone(final ? 660 : 340, final ? 0.16 : 0.08, "square", 0.03, final ? 260 : 0);
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    frequencyDelta: number,
    delay = 0,
  ): void {
    if (!this.enabled || !this.context) return;

    const now = this.context.currentTime + delay;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(40, frequency + frequencyDelta), now + duration);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }
}
